import "./CustomerShow/style.scss";
import PersonalDetails from "./CustomerShow/PersonalDetails";
import BusinessDetails from "./CustomerShow/BusinessDetails";
import SubscriptionDetails from "./CustomerShow/SubscriptionDetails";
import logger from "../../config/Logging";
import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Customer } from "../../models/Customer";
import { Business, BusinessSubscription } from "../../models/Business";
import { CustomerObjectMapType } from "../../util/providers/CustomerProvider";
import { useCustomerObjectMap } from "../../hooks/useCustomerObjectMap";
import { LoadingPage } from "../LoadingPage";
import {
  PageHeader,
  Breadcrumb,
  Button,
  Card,
  Col,
  Menu,
  Row,
  Tooltip,
  Alert,
  notification,
  Space,
  Form,
} from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Key } from "antd/lib/table/interface";
import Modal from "antd/lib/modal";
import { patch, post } from "../../config/Axios";
import { getUnixTime } from "date-fns";
import {
  CanNotCancelModalProps,
  ConfirmCancelModalProps,
} from "./CustomerShow/CancelModal";
import { Store } from "antd/lib/form/interface";
import Stripe from "stripe";

type HistoryState = {
  customer?: Customer;
};

const CustomerShow: React.FC = () => {
  const history = useHistory<HistoryState>();
  const params = useParams<{ sub: string }>();
  const {
    customers,
    loading: customersLoading,
    refetch,
  } = useCustomerObjectMap();
  const [customer, setCustomer] = useState<CustomerObjectMapType>();
  const [business, setBusiness] = useState<Business>();
  const [activeTab, setActiveTab] = useState<Key>("1");
  const [error, setError] = useState<string>();
  const cancelling = business?.subscription?.cancel_at_period_end;
  const active = ["active", "trialing"].includes(
    business?.subscription?.status || ""
  );
  const canNotBeCancelled = !active || cancelling;
  const [form] = Form.useForm();

  useEffect(() => {
    if (!params) return;
    if (customersLoading) return;

    const currentCustomer = customers?.find(
      (entry) => entry.sub === params.sub
    );
    if (currentCustomer) {
      setCustomer(currentCustomer);
      if (currentCustomer.businesses && currentCustomer.businesses.length > 0) {
        const latestBusiness =
          currentCustomer.businesses[currentCustomer.businesses.length - 1];
        setBusiness(latestBusiness);
      } else {
        setBusiness(undefined);
      }
    } else {
      setError("Failed to find customer");
      logger.log("error", "Get customer", {
        additionalInfo: {
          tags: "customer",
          body: "Could not find customer for Trade partner",
          file: "Pages.Partner.CustomerPage",
          user: params.sub,
        },
      });
    }
  }, [customers, customersLoading, params]);

  const handleSubscriptionCancel = async (values: Store) => {
    const subscription = business?.subscription;
    const { lateCancellation } = values;

    if (!subscription) throw new Error("No Subscription");
    if (!business?.id) throw new Error("No business");

    // Invoiced trade customer, pays trade partner directly

    const price = subscription.items.data[0].price;
    const product = price?.product as Stripe.Product;
    const isInvoiced =
      product?.metadata.gift_eligible === "1" ||
      price?.metadata.trade === "true";

    if (isInvoiced) {
      try {
        await post({
          path: "/stripe/subscription/cancel",
          data: {
            id: subscription.id,
            instant: true,
          },
        });
        const updatedSubscription: BusinessSubscription = {
          ...subscription,
          status: "canceled",
          canceled_at: getUnixTime(new Date()),
        };
        setBusiness({
          ...business,
          subscription: updatedSubscription,
        });
        notification.success({
          message: "iLivestock subscription cancelled",
          placement: "topRight",
        });
        refetch({ clearData: true });
      } catch (error) {
        logger.log("error", "Cancel invoiced subscription", {
          additionalInfo: {
            tags: "subscription",
            body: error,
            file: "Pages.Customer.Business.BusinessPage",
            user: customer?.sub,
          },
        });
        notification.error({
          message: "Error cancelling invoiced subscription. Please try again",
          placement: "topRight",
        });
        return;
      } finally {
        form.resetFields();
      }
      return;
    }

    // Direct trade customer (pays by card through Stripe)
    try {
      await patch({
        path: "/stripe/subscription",
        data: {
          subscription: subscription.id,
          action: subscription.schedule ? "release" : "cancel",
          lateCancellation,
        },
      });
      const updatedSubscription: BusinessSubscription = {
        ...subscription,
        cancel_at_period_end: true,
        canceled_at: getUnixTime(new Date()),
      };
      setBusiness({
        ...business,
        subscription: updatedSubscription,
      });
      notification.success({
        message: "iLivestock subscription cancelled",
        placement: "topRight",
      });
      refetch({ clearData: true });
    } catch (error) {
      logger.log("error", "Cancel direct subscription", {
        additionalInfo: {
          tags: "subscription",
          body: error,
          file: "Pages.Customer.Business.BusinessPage",
          user: customer?.sub,
        },
      });
      notification.error({
        message: "Error cancelling direct subscription. Please try again",
        placement: "topRight",
      });
      return;
    } finally {
      form.resetFields();
    }
  };

  if (error) {
    console.error(error);
    return (
      <Alert
        type="error"
        showIcon
        message="There was an error loading your data."
      />
    );
  }

  const buildSubscriptionLabel = (
    subscription?: BusinessSubscription
  ): string => {
    if (!subscription) return "";

    if (subscription.status === "active" && subscription.canceled_at)
      return `${subscription.plan.name} - Active (Cancelling)`;
    if (subscription.status === "trialing" && subscription.canceled_at)
      return `${subscription.plan.name} - Trialing (Cancelling)`;
    if (subscription.status === "active")
      return `${subscription.plan.name} - Active`;
    if (subscription.status === "trialing")
      return `${subscription.plan.name} - Trialing`;

    return `${subscription.plan.name} - Unknown status`;
  };

  return (
    <Row gutter={[16, 24]}>
      <Col xs={24}>
        <PageHeader
          className="site-page-header"
          style={{ padding: 0 }}
          title="Customers"
          subTitle="Show details"
          extra={[
            <Space>
              <Button
                key="back"
                type="primary"
                onClick={() => {
                  history.push("/");
                }}
              >
                Back
              </Button>
            </Space>,
          ]}
        >
          <Row>
            <Col xs={24}>
              <Card size="small">
                <Breadcrumb>
                  <Breadcrumb.Item>
                    <a onClick={() => history.push("/")}>Customers</a>
                  </Breadcrumb.Item>
                  <Breadcrumb.Item>{params.sub}</Breadcrumb.Item>
                </Breadcrumb>
              </Card>
            </Col>
          </Row>
        </PageHeader>
      </Col>

      {!customer ? (
        <LoadingPage tip="Loading customer" />
      ) : (
        <Col xs={24}>
          <Card
            headStyle={{
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
            }}
            bodyStyle={{ padding: 0 }}
            className="mb-4 overflow-hidden w-100 customer-details header"
            title={
              <Row align="middle">
                <div
                  className="px-0 text-light"
                  style={{
                    display: "inline-block",
                  }}
                >
                  <h5 className="my-0">
                    <span>{customer.givenName || customer.email}</span>
                    <b> {customer.familyName}</b>
                  </h5>
                  <small>
                    {business?.subscription ? (
                      buildSubscriptionLabel(business?.subscription)
                    ) : (
                      <span className="flex items-center">
                        No subscription
                        <Tooltip title="This customer has no subscription. Check their account setup status">
                          <ExclamationCircleFilled
                            style={{
                              color: "Red",
                              fontSize: "1.2em",
                              marginLeft: 8,
                            }}
                          />
                        </Tooltip>
                      </span>
                    )}
                  </small>
                </div>
              </Row>
            }
            extra={
              <Row align="middle" className="p-0">
                {business?.subscription && (
                  <Button
                    type="primary"
                    danger={
                      (business.subscription.status === "active" ||
                        business.subscription.status === "trialing") &&
                      !cancelling
                    }
                    onClick={() => {
                      if (canNotBeCancelled) {
                        Modal.error(
                          CanNotCancelModalProps({ cancelling, business })
                        );
                      } else {
                        Modal.confirm(
                          ConfirmCancelModalProps({
                            form,
                            customer,
                            business,
                            handleSubscriptionCancel,
                          })
                        );
                      }
                    }}
                  >
                    Cancel subscription
                  </Button>
                )}
              </Row>
            }
          >
            <Menu
              onClick={(tab) => {
                if (activeTab !== tab.key) setActiveTab(tab.key);
              }}
              selectedKeys={[activeTab.toString()]}
              mode="horizontal"
              className="border-bottom-0"
            >
              <Menu.Item key="1">Personal details</Menu.Item>
              <Menu.Item key="2" disabled={!business}>
                Business details
              </Menu.Item>
              <Menu.Item key="3" disabled={!business?.subscription}>
                Subscription
              </Menu.Item>
            </Menu>
          </Card>
        </Col>
      )}

      {activeTab === "1" && (
        <Col xs={24}>
          <PersonalDetails customer={customer} />
        </Col>
      )}

      {activeTab === "2" && (
        <Col xs={24}>
          <BusinessDetails business={business} />
        </Col>
      )}

      {activeTab === "3" && (
        <Col xs={24}>
          <SubscriptionDetails business={business} />
        </Col>
      )}
    </Row>
  );
};

export default CustomerShow;
